// File: /pages/ShuffleMaps/Classes.jsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import styles from "./Classes.module.css";
import ListingCard from "/components/Shufflemaps/Listings/ListingCard";

const PAGE_SIZE = 24;

export default function ClassesPage() {
  const [allListings, setAllListings] = useState([]); // raw payload
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest"); // 'newest' | 'rating' | 'popular'
  const [page, setPage] = useState(1); // client-side pagination

  const topRef = useRef(null);

  // Fetch once from existing listings endpoint
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/shufflemaps/listings/get-listings", {
          credentials: "include",
          cache: "no-store",
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!alive) return;
        setAllListings(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!alive) return;
        setErrorMsg(e?.message || "Failed to load listings");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Filter to "Classes" category without guessing DB internals
  const classListings = useMemo(() => {
    return (allListings || []).filter((l) =>
      Array.isArray(l.categories) ? l.categories.includes("Classes") : false
    );
  }, [allListings]);

  // Apply search
  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return classListings;
    return classListings.filter((l) => {
      const fields = [
        l.name,
        l.short_description,
        l.description,
        l.address,
        l.city,
        l.admin1_code,
        l.country_code,
        Array.isArray(l.categories) ? l.categories.join(" ") : "",
        Array.isArray(l.tags) ? l.tags.map((t) => t?.name || t).join(" ") : "",
      ];
      return fields.some((f) => String(f || "").toLowerCase().includes(q));
    });
  }, [classListings, query]);

  // Apply sorting
  const sorted = useMemo(() => {
    const list = [...searched];
    if (sort === "rating") {
      // If rating fields are present, prefer them; otherwise leave order
      list.sort((a, b) => {
        const ar = Number(a.rating ?? -1);
        const br = Number(b.rating ?? -1);
        if (ar !== br) return br - ar;
        const ac = Number(a.review_count ?? -1);
        const bc = Number(b.review_count ?? -1);
        return bc - ac;
      });
    } else if (sort === "popular") {
      list.sort((a, b) => {
        const ac = Number(a.review_count ?? -1);
        const bc = Number(b.review_count ?? -1);
        if (ac !== bc) return bc - ac;
        const ar = Number(a.rating ?? -1);
        const br = Number(b.rating ?? -1);
        return br - ar;
      });
    } else {
      // newest (fallback to original order if updated_at not present)
      list.sort((a, b) => {
        const ad = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const bd = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return bd - ad;
      });
    }
    return list;
  }, [searched, sort]);

  // Client-side pagination
  const total = sorted.length;
  const visible = useMemo(
    () => sorted.slice(0, page * PAGE_SIZE),
    [sorted, page]
  );
  const hasMore = visible.length < total;

  // Handlers
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    // optional scroll-to-top on new search
    requestAnimationFrame(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleSortChange = (e) => {
    setSort(e.target.value);
    setPage(1);
    requestAnimationFrame(() => {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const loadMore = () => setPage((p) => p + 1);

  return (
    <main className={styles.smMain}>
      <section ref={topRef} className={styles.smSection}>
        <h1 className={styles.smTitle}>Classes</h1>

        <form className={styles.toolbar} onSubmit={handleSearchSubmit}>
          <input
            className={styles.input}
            placeholder="Search classes (city, studio, instructor)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select className={styles.select} value={sort} onChange={handleSortChange}>
            <option value="newest">Newest</option>
            <option value="rating">Highest Rated</option>
            <option value="popular">Most Popular</option>
            {/* <option value="nearest">Nearest</option> */}
          </select>
          <button className={styles.btn} type="submit">
            Search
          </button>
        </form>

        {loading ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>Loading…</div>
            <div className={styles.emptySub}>Fetching class listings.</div>
          </div>
        ) : errorMsg ? (
          <div className={styles.errorBox}>
            {errorMsg}
          </div>
        ) : total === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>No class listings found</div>
            <div className={styles.emptySub}>
              Try a different search term or clear your filters.
            </div>
          </div>
        ) : (
          <>
            <div className={styles.resultsHeader}>
              <div className={styles.resultsTitle}>
                {visible.length} of {total} classes
              </div>
              <div className={styles.resultsMeta}>
                {query ? <>Filtered by “{query}”</> : <>Showing all</>}
              </div>
            </div>

            <div className={styles.cardsGrid}>
              {visible.map((l) => (
                <Link
                  key={l.id}
                  href={{ pathname: "/ShuffleMaps", query: { listingId: String(l.id) } }}
                  className={styles.cardWrap}
                  title="Open in ShuffleMaps"
                >
                  <ListingCard
                    listingId={l.id}
                    name={l.name}
                    status={l.status}
                    shortDescription={l.short_description ?? l.shortDescription}
                    suppressEmptyRatingDisplay
                  />
                </Link>
              ))}
            </div>

            {hasMore && (
              <div className={styles.loadMoreRow}>
                <button className={styles.loadMoreBtn} onClick={loadMore}>
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
